import { isContentMax, isMobile } from "components/utility/page-responsive/PageResponsive";

const calculatePercent = (
	microCapExhibitBoundingRect: DOMRect,
	svg: SVGElement,
	side: "left" | "bottom",
	label: "start" | "end"
): string => {
	// Finds the percent distance between the start of the svg and the left/bottom of the container
	const startingCirlceBoundingRect = svg!
		.querySelector<SVGGeometryElement>(".starting-point")!
		.getBoundingClientRect();
	const endingCircleBoundingRect = svg!
		.querySelector<SVGGeometryElement>(".end-point")!
		.getBoundingClientRect();

	const circleToUse = label === "start" ? startingCirlceBoundingRect : endingCircleBoundingRect;
	let percent;
	if (side === "left") {
		const leftDistance =
			circleToUse === startingCirlceBoundingRect ? circleToUse.left + 3 : circleToUse.left;
		percent =
			((leftDistance - microCapExhibitBoundingRect.left) / microCapExhibitBoundingRect.width) * 100;
	} else {
		percent =
			((microCapExhibitBoundingRect.bottom - circleToUse.bottom) /
				microCapExhibitBoundingRect.height) *
			100;
	}
	return `${percent?.toFixed(3)}%`;
};

const calculateDistance = (
	label: HTMLDivElement,
	labelName: "fund" | "index",
	direction: "width" | "height"
): number => {
	// Finds the px distance between the start of the value label and the start of the dot at the end of the svg
	let distance = 0;
	const valueLabel = label.querySelector<HTMLDivElement>(
		`.micro-cap-svg-${labelName}-end-content-value`
	)!;
	const labelBoundingRect = valueLabel.getBoundingClientRect();
	const endingCircleBoundingRect = document
		.querySelector<SVGAElement>(`.micro-cap-svg-${labelName}-end-xs-dot`)!
		.getBoundingClientRect();
	if (direction === "width") {
		distance =
			endingCircleBoundingRect.left - labelBoundingRect.right - endingCircleBoundingRect.width;
	} else {
		distance =
			endingCircleBoundingRect.bottom - (labelBoundingRect.bottom + labelBoundingRect.top) / 2;
	}
	return distance;
};

const swapSubCopyFontSize = (label: HTMLDivElement): void => {
	// Swaps t-body-sm to t-body-xs and vice versa depending on viewport size
	const subCopy = label.querySelector(".t-body-sm,.t-body-xs")!;
	if (isContentMax()) {
		subCopy.classList.remove("t-body-xs");
		subCopy.classList.add("t-body-sm");
	} else {
		subCopy.classList.remove("t-body-sm");
		subCopy.classList.add("t-body-xs");
	}
};

const placeStartLabels = (
	microCapExhibit: HTMLDivElement,
	microCapExhibitBoundingRect: DOMRect,
	svg: SVGElement
): void => {
	// Calls calculatePercent to add bottom and left properties for the label to placed at the start of the svg
	const fundStartLabel = microCapExhibit.querySelector<HTMLDivElement>(
		".micro-cap-svg-fund-start"
	)!;
	const indexStartLabel = microCapExhibit.querySelector<HTMLDivElement>(
		".micro-cap-svg-index-start"
	)!;

	const startLabel = svg.classList[0] === "micro-cap-svg-fund" ? fundStartLabel : indexStartLabel;

	startLabel!.style.bottom = calculatePercent(microCapExhibitBoundingRect, svg, "bottom", "start");

	startLabel.style.left = calculatePercent(microCapExhibitBoundingRect, svg, "left", "start");
};

const placeEndLabelsDefault = (
	endLabel: HTMLDivElement,
	microCapExhibitBoundingRect: DOMRect,
	svg: SVGElement
): void => {
	// Calls calculatePercent to add bottom and left properties for the label to placed at the end of the svg when bp is not xs
	endLabel.style.removeProperty("top");
	endLabel.style.removeProperty("width");
	endLabel.style.bottom = calculatePercent(microCapExhibitBoundingRect, svg, "bottom", "end");
	endLabel.style.left = calculatePercent(microCapExhibitBoundingRect, svg, "left", "end");
};

const placeEndLabelsXS = (
	microCapExhibit: HTMLDivElement,
	microCapExhibitBoundingRect: DOMRect,
	endLabel: HTMLDivElement,
	svg: SVGElement,
	isFundEndLabel: boolean
): void => {
	// Calls calculatePercent to add bottom and left properties for the label to placed at the end of the svg when bp is xs
	let endDot;
	if (isFundEndLabel) {
		endDot = microCapExhibit.querySelector<SVGAElement>(".micro-cap-svg-fund-end-xs-dot")!;
		endLabel.style.removeProperty("bottom");
		endLabel.style.top = "0";
	} else {
		endDot = microCapExhibit.querySelector<SVGAElement>(".micro-cap-svg-index-end-xs-dot")!;
		endLabel.style.bottom = calculatePercent(microCapExhibitBoundingRect, svg, "bottom", "end");
	}

	endLabel.style.left = "0";
	endDot.style.bottom = calculatePercent(microCapExhibitBoundingRect, svg, "bottom", "end");
	endDot.style.left = calculatePercent(microCapExhibitBoundingRect, svg, "left", "end");
};

const paintDottedLines = (
	microCapExhibit: HTMLDivElement,
	endLabel: HTMLDivElement,
	isFundEndLabel: boolean
): void => {
	// Sets the width / height for the svg dotted line that points to the end dot at xs at each graph
	let dottedLine;
	let distanceFromLabelToDot;
	let leftValue;
	const gapBetweenLabelAndDottedLine = 12;

	if (isFundEndLabel) {
		dottedLine = microCapExhibit.querySelector<SVGAElement>(
			".micro-cap-svg-fund-end-content-dotted-line"
		)!;
		distanceFromLabelToDot = calculateDistance(endLabel, "fund", "width");
		leftValue = endLabel
			.querySelector<HTMLDivElement>(`.micro-cap-svg-fund-end-content-value`)!
			.getBoundingClientRect().width;
		dottedLine
			.querySelector("path")!
			.setAttribute(
				"d",
				`M2 1 L${distanceFromLabelToDot - 2} 1 L ${distanceFromLabelToDot - 2} 1000`
			);
		dottedLine.style.height = `${calculateDistance(endLabel, "fund", "height") / 16}rem`;
	} else {
		dottedLine = microCapExhibit.querySelector<SVGAElement>(
			".micro-cap-svg-index-end-content-dotted-line"
		)!;
		distanceFromLabelToDot = calculateDistance(endLabel, "index", "width");
		leftValue = endLabel
			.querySelector<HTMLDivElement>(`.micro-cap-svg-index-end-content-value`)!
			.getBoundingClientRect().width;
	}

	dottedLine.style.width = `${distanceFromLabelToDot / 16}rem`;
	dottedLine.style.left = `${(leftValue + gapBetweenLabelAndDottedLine) / 16}rem`;
};

const applyStyles = (microCapExhibit: HTMLDivElement, svgArray: SVGElement[]): void => {
	// Calls helper functions based on bp size to draw place the labels and dotted lines correctly
	const fundEndLabel = microCapExhibit.querySelector<HTMLDivElement>(".micro-cap-svg-fund-end")!;
	const indexEndlabel = microCapExhibit.querySelector<HTMLDivElement>(".micro-cap-svg-index-end")!;
	const microCapExhibitBoundingRect: DOMRect = microCapExhibit.getBoundingClientRect();

	svgArray.forEach((svg) => {
		placeStartLabels(microCapExhibit, microCapExhibitBoundingRect, svg);

		const endLabel = svg.classList[0] === "micro-cap-svg-fund" ? fundEndLabel : indexEndlabel;
		swapSubCopyFontSize(endLabel);

		if (!isMobile()) {
			placeEndLabelsDefault(endLabel, microCapExhibitBoundingRect, svg);
		} else {
			const isFundEndLabel = endLabel === fundEndLabel;
			placeEndLabelsXS(microCapExhibit, microCapExhibitBoundingRect, endLabel, svg, isFundEndLabel);
			paintDottedLines(microCapExhibit, endLabel, isFundEndLabel);
		}
	});
};

const positionMicroCapLabels = () => {
	const microCapExhibit = document.querySelector<HTMLDivElement>(".ddiff-micro-cap-chart-exhibit")!;
	const fundSvg = microCapExhibit.querySelector<SVGElement>(".micro-cap-svg-fund")!;
	const indexSvg = microCapExhibit.querySelector<SVGElement>(".micro-cap-svg-index")!;

	applyStyles(microCapExhibit, [fundSvg, indexSvg]);

	window.addEventListener("resize", positionMicroCapLabels, { once: true });
};

export default positionMicroCapLabels;
